import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    Users,
    Clock,
    CheckCircle2,
    MessageCircle,
    Activity,
    Settings,
    MapPin,
    Bot,
    Trophy,
    Hash,
    RefreshCw,
    ChevronDown,
    ChevronUp,
    TicketPercent,
    User,
    Calendar,
    Settings2,
    X,
    Timer,
    Save
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { getStaffDisplayName } from '../../utils/staffDisplay';

const TeamManagement = () => {
    const { user } = useAuth();
    const [stats, setStats] = useState({
        total24h: 0,
        triagem: 0,
        queue: 0,
        inProgress: 0,
        closedToday: 0,
        awaitingStaff: 0,
        awaitingUser: 0,
        botTickets: 0,
        byCity: {},
        topCategories: {
            queue: null,
            progress: null,
            awaiting: null
        },
        botCupomStats: {
            resolvidosSemStaff: 0,
            emAtendimento: 0,
            paraStaff: 0
        }
    });
    const [staffList, setStaffList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSavingFlow, setIsSavingFlow] = useState(false);
    const [expandedCities, setExpandedCities] = useState({});

    // Cupom 70 Control Settings
    const [isCupomModalOpen, setIsCupomModalOpen] = useState(false);
    const [cupomSettings, setCupomSettings] = useState({
        status: 'active', // 'active', 'suspended_manual', 'suspended_auto'
        autoSuspendAt: '',
        showTimer: false
    });
    const [isSavingCupom, setIsSavingCupom] = useState(false);

    // Queue Flow Control Settings
    const [flowThreshold, setFlowThreshold] = useState(20);
    const [restrictedCities, setRestrictedCities] = useState([]);
    const availableCities = ['CAPITAL', 'VALLEY NORTE', 'VALLEY SUL', 'MORADA DO VALLEY', 'GERAL'];

    const fetchDashboardData = async () => {
        try {
            // 1. Fetch Staff Activity
            const { data: staffData, error: staffError } = await supabase
                .from('users')
                .select('id, discord_id, name, username, last_login, avatar, access_level, is_staff')
                .or('is_staff.eq.true,access_level.in.(suporte,suporte_compras,suporte_descontos,moderador,super_moderador,programador,head_suporte,head_moderacao,head_staff,admin,direcao)')
                .order('last_login', { ascending: false });

            if (!staffError && staffData) {
                const now = new Date().getTime();
                const processedStaff = staffData.map(s => {
                    const lastActive = s.last_login ? new Date(s.last_login).getTime() : 0;
                    const diffMins = (now - lastActive) / (1000 * 60);

                    let status = 'offline';
                    if (diffMins <= 15) status = 'active';
                    else if (diffMins <= 30) status = 'away';

                    return { ...s, status, diffMins };
                }).filter(s => s.status !== 'offline'); // Only show active/away or recently offline if desired. Let's show active and away.

                setStaffList(processedStaff);
            }

            // 2. Fetch Ticket Stats
            const yesterdayStart = new Date();
            yesterdayStart.setHours(yesterdayStart.getHours() - 24);

            const { data: activeData } = await supabase
                .from('tickets')
                .select('id, city, category, status, assigned_to, auxiliary_status')
                .not('status', 'in', '("fechado","resolvido")');

            const { data: closedData } = await supabase
                .from('tickets')
                .select('id, city, category, status, assigned_to, auxiliary_status')
                .in('status', ['fechado', 'resolvido'])
                .gte('last_update', yesterdayStart.toISOString());

            const allTickets = [...(activeData || []), ...(closedData || [])];

            const { count: total24hCount } = await supabase
                .from('tickets')
                .select('*', { count: 'exact', head: true })
                .gte('created_at', yesterdayStart.toISOString());

            let globalQueue = 0, globalProgress = 0, globalClosed = 0, globalAwaiting = 0, globalBot = 0, globalAwaitingUser = 0, globalTriagem = 0;
            let cupomResSemStaff = 0, cupomInatividade = 0, cupomEmAtend = 0, cupomParaStaff = 0;

            const cityStats = {};
            const rawQueue = [];
            const rawProgress = [];
            const rawAwaiting = [];

            allTickets.forEach(t => {
                const isClosed = ['fechado', 'resolvido'].includes(t.status);
                const isQueue = t.status === 'aberto' && !t.assigned_to;

                // Globals
                if (isClosed) globalClosed++;
                else if (isQueue) globalQueue++;
                else if (t.status === 'aguardando_sistema') globalBot++;
                else if (t.status === 'aguardando_usuario') globalAwaitingUser++;
                else if (t.status === 'aguardando_staff') globalAwaiting++;
                else if (t.status === 'verificacao') globalTriagem++;
                else if (t.assigned_to && t.status !== 'aguardando_sistema') globalProgress++;

                // Collect for Top Categories
                if (isQueue) rawQueue.push(t);
                if (t.assigned_to && !isClosed && t.status !== 'aguardando_sistema') rawProgress.push(t);
                if (t.status === 'aguardando_staff') rawAwaiting.push(t);

                // Bot Cupom Stats
                if (t.category === 'cupons_descontos' || t.category === 'cupom_70' || t.category === 'super_cupom') {
                    const isInativo = t.auxiliary_status && t.auxiliary_status.includes('fechado_inatividade');
                    if (isClosed && !t.assigned_to) {
                        if (isInativo) cupomInatividade++;
                        else cupomResSemStaff++;
                    }
                    if (t.status === 'aguardando_sistema') cupomEmAtend++;
                    if (t.status === 'aguardando_staff' || isQueue || (t.assigned_to && !isClosed)) cupomParaStaff++; // Needs or is with Staff
                }

                // Deep City/Cat Aggregate
                const cty = t.city || 'GLOBAL';
                const cat = t.category || 'Não definida';

                if (!cityStats[cty]) cityStats[cty] = {};
                if (!cityStats[cty][cat]) {
                    cityStats[cty][cat] = {
                        botAtendimento: 0,
                        fila: 0,
                        aguardandoUser: 0,
                        aguardandoStaff: 0,
                        ativos: 0,
                        fechados: 0
                    };
                }

                const c = cityStats[cty][cat];
                if (t.status === 'aguardando_sistema') c.botAtendimento++;
                if (isQueue) c.fila++;
                if (t.status === 'aguardando_usuario') c.aguardandoUser++;
                if (t.status === 'aguardando_staff') c.aguardandoStaff++;
                if (t.assigned_to && !isClosed && t.status !== 'aguardando_sistema') c.ativos++;
                if (isClosed) c.fechados++;
            });

            // Top Category By State Helper
            const findTopCategory = (dataArray) => {
                if (!dataArray || dataArray.length === 0) return null;
                const catCounts = {};
                dataArray.forEach(t => {
                    const cat = t.category || 'Não definida';
                    const city = t.city || 'GLOBAL';
                    const key = `${cat}|${city}`;
                    catCounts[key] = (catCounts[key] || 0) + 1;
                });
                let top = null;
                let max = 0;
                for (const [key, val] of Object.entries(catCounts)) {
                    if (val > max) {
                        max = val;
                        const [c, cty] = key.split('|');
                        top = { category: c, city: cty, count: val };
                    }
                }
                return top;
            };

            setStats({
                total24h: total24hCount || 0,
                triagem: globalTriagem,
                queue: globalQueue,
                inProgress: globalProgress,
                closedToday: globalClosed,
                awaitingStaff: globalAwaiting,
                awaitingUser: globalAwaitingUser,
                botTickets: globalBot,
                byCity: cityStats,
                topCategories: {
                    queue: findTopCategory(rawQueue),
                    progress: findTopCategory(rawProgress),
                    awaiting: findTopCategory(rawAwaiting)
                },
                botCupomStats: {
                    resolvidosSemStaff: cupomResSemStaff,
                    inatividade: cupomInatividade,
                    emAtendimento: cupomEmAtend,
                    paraStaff: cupomParaStaff
                }
            });

        } catch (err) {
            console.error('Error fetching dashboard data:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchFlowSettings = async () => {
        const { data } = await supabase.from('system_settings').select('value').eq('key', 'queue_flow_control').single();
        if (data && data.value) {
            setFlowThreshold(data.value.threshold || 20);
            setRestrictedCities(data.value.restrictedCities || []);
        }

        const { data: cupomData } = await supabase.from('system_settings').select('value').eq('key', 'cupom70_status').single();
        if (cupomData && cupomData.value) {
            setCupomSettings(cupomData.value);
        }
    };

    const saveCupomSettings = async () => {
        setIsSavingCupom(true);
        try {
            const { error } = await supabase.from('system_settings').upsert({
                key: 'cupom70_status',
                value: cupomSettings,
                updated_at: new Date().toISOString()
            });

            if (error) throw error;
            toast.success('Configuração da categoria Cupom 70 salva!');
            setIsCupomModalOpen(false);
        } catch (e) {
            console.error(e);
            toast.error('Erro ao salvar Cupom 70.');
        } finally {
            setIsSavingCupom(false);
        }
    };

    const saveFlowSettings = async () => {
        setIsSavingFlow(true);
        try {
            const payload = {
                threshold: parseInt(flowThreshold, 10) || 20,
                restrictedCities: restrictedCities
            };

            const { error } = await supabase.from('system_settings').upsert({
                key: 'queue_flow_control',
                value: payload,
                updated_at: new Date().toISOString()
            });

            if (error) throw error;
            toast.success('Regras de fluxo atualizadas com sucesso!');
        } catch (e) {
            console.error(e);
            toast.error('Erro ao salvar configurações de fluxo.');
        } finally {
            setIsSavingFlow(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
        fetchFlowSettings();
        const interval = setInterval(fetchDashboardData, 60000); // 60 seconds polling
        return () => clearInterval(interval);
    }, []);

    const toggleCityExpanded = (city) => {
        setExpandedCities(prev => ({ ...prev, [city]: !prev[city] }));
    };

    const getStatusColor = (status) => {
        if (status === 'active') return 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]';
        if (status === 'away') return 'bg-yellow-500 shadow-[0_0_8px_rgba(234,179,8,0.4)]';
        return 'bg-gray-500';
    };

    const getStatusLabel = (status) => {
        if (status === 'active') return 'Ativo Agora';
        if (status === 'away') return 'Ausente (<30m)';
        return 'Offline';
    };

    const StatCard = ({ title, value, icon: Icon, color, delay }) => (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}>
            <Card className="bg-black/40 border-white/10 backdrop-blur-md overflow-hidden relative group h-full">
                <div className={`absolute inset-0 bg-gradient-to-br ${color} opacity-5 group-hover:opacity-10 transition-opacity`} />
                <CardContent className="p-4 flex flex-col justify-between h-full">
                    <p className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-2">{title}</p>
                    <div className="flex justify-between items-end mt-2">
                        <h3 className="text-3xl font-black text-white">{value}</h3>
                        <div className={`p-2 rounded-xl bg-gradient-to-br ${color} bg-opacity-10`}>
                            <Icon className="w-5 h-5 text-white" />
                        </div>
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );

    return (
        <div className="space-y-6 max-w-7xl mx-auto pb-12">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                        <Users className="w-8 h-8 text-indigo-500" />
                        Gestão da Equipe & Fluxo
                    </h1>
                    <p className="text-gray-400 mt-1">Monitoramento em tempo real (Atualiza a cada 60s)</p>
                </div>
                <Button onClick={fetchDashboardData} disabled={isLoading} variant="outline" className="border-white/10 text-white bg-white/5 hover:bg-white/10">
                    <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                    Atualizar Manualmente
                </Button>
            </div>

            {/* STATS ROW */}
            <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-4">
                <StatCard title="TOTAL (24H)" value={stats.total24h} icon={Calendar} color="from-gray-500 to-gray-700" delay={0.05} />
                <StatCard title="TRIAGEM" value={stats.triagem} icon={Activity} color="from-red-600 to-rose-700" delay={0.08} />
                <StatCard title="FILA (NOVOS)" value={stats.queue} icon={Clock} color="from-yellow-600 to-orange-600" delay={0.1} />
                <StatCard title="ATEND. (BOT)" value={stats.botTickets} icon={Bot} color="from-slate-500 to-gray-600" delay={0.15} />
                <StatCard title="ATEND. (STAFF)" value={stats.inProgress} icon={Activity} color="from-blue-600 to-indigo-600" delay={0.2} />
                <StatCard title="AGUARD. (USUÁRIO)" value={stats.awaitingUser} icon={User} color="from-pink-500 to-rose-600" delay={0.25} />
                <StatCard title="AGUARD. (STAFF)" value={stats.awaitingStaff} icon={MessageCircle} color="from-purple-600 to-pink-600" delay={0.3} />
                <StatCard title="RESOLVIDOS (24H)" value={stats.closedToday} icon={CheckCircle2} color="from-emerald-600 to-teal-600" delay={0.4} />
            </div>

            {/* BOT CUPOM STATS */}
            <Card className="bg-[#0F0F10] border-emerald-500/20 shadow-lg shadow-emerald-500/5">
                <CardContent className="p-4 flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-full bg-emerald-500/10 hidden md:block">
                            <TicketPercent className="w-8 h-8 text-emerald-500" />
                        </div>
                        <div>
                            <p className="text-emerald-500 font-bold uppercase tracking-widest text-sm flex items-center gap-2">
                                Bot Cupons & Descontos
                                <Button size="sm" variant="ghost" className="h-6 px-2 text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/20 bg-emerald-500/10 rounded" onClick={() => setIsCupomModalOpen(true)}>
                                    <Settings2 className="w-3.5 h-3.5 mr-1" /> Gerenciar
                                </Button>
                            </p>
                            <p className="text-gray-400 text-xs mt-1">Desempenho de resolução automática (Hoje)</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 md:gap-8 bg-black/30 rounded-lg p-3 w-full md:w-auto overflow-hidden">
                        <div className="text-center">
                            <p className="text-sm font-bold text-white mb-1"><span className="text-emerald-400">{stats.botCupomStats.resolvidosSemStaff}</span> 🚀</p>
                            <p className="text-[10px] text-gray-400 uppercase">Resolvido</p>
                        </div>
                        <div className="w-px h-8 bg-white/10 hidden md:block"></div>
                        <div className="text-center">
                            <p className="text-sm font-bold text-white mb-1"><span className="text-gray-400">{stats.botCupomStats.inatividade}</span> ⌛</p>
                            <p className="text-[10px] text-gray-400 uppercase">Por Inatividade</p>
                        </div>
                        <div className="w-px h-8 bg-white/10 hidden md:block"></div>
                        <div className="text-center">
                            <p className="text-sm font-bold text-white mb-1"><span className="text-blue-400">{stats.botCupomStats.emAtendimento}</span> 🤖</p>
                            <p className="text-[10px] text-gray-400 uppercase">Em Atend. (Bot)</p>
                        </div>
                        <div className="w-px h-8 bg-white/10 hidden md:block"></div>
                        <div className="text-center">
                            <p className="text-sm font-bold text-white mb-1"><span className="text-orange-400">{stats.botCupomStats.paraStaff}</span> 👤</p>
                            <p className="text-[10px] text-gray-400 uppercase">Aguardando Staff</p>
                        </div>
                        <div className="w-px h-8 bg-white/10 hidden md:block"></div>
                        <div className="text-center">
                            <p className="text-sm font-bold text-white mb-1">
                                <span className="text-emerald-300">
                                    {((stats.botCupomStats.resolvidosSemStaff + stats.botCupomStats.inatividade) / Math.max(1, (stats.botCupomStats.resolvidosSemStaff + stats.botCupomStats.inatividade + stats.botCupomStats.paraStaff + stats.botCupomStats.emAtendimento)) * 100).toFixed(0)}%
                                </span> 📈
                            </p>
                            <p className="text-[10px] text-gray-400 uppercase">Retenção (Bot)</p>
                        </div>
                    </div>
                </CardContent>
            </Card>


            {/* CITY DETAILS MULTIPLE CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {Object.entries(stats.byCity).length === 0 && !isLoading && (
                    <div className="col-span-full p-6 text-center text-gray-500 font-medium">Nenhum dado das cidades</div>
                )}
                {Object.entries(stats.byCity).map(([city, categoriesData]) => {
                    const isExpanded = !!expandedCities[city];

                    return (
                        <Card key={city} className={`bg-[#0F0F10] border-white/10 flex flex-col h-fit transition-all duration-300 ${isExpanded ? 'ring-1 ring-indigo-500/50 shadow-[0_0_20px_rgba(99,102,241,0.15)] bg-gradient-to-b from-[#1a1b26] to-[#0F0F10]' : 'hover:border-white/20'}`}>
                            <CardHeader
                                className="border-b border-white/5 p-4 shrink-0 cursor-pointer hover:bg-white/[0.04] transition-colors rounded-t-xl"
                                onClick={() => toggleCityExpanded(city)}
                            >
                                <CardTitle className="text-lg font-bold flex items-center justify-between group">
                                    <div className="flex items-center gap-2 text-white"><MapPin className="w-5 h-5 text-indigo-400 group-hover:scale-110 transition-transform" /> {city}</div>
                                    {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                                </CardTitle>
                            </CardHeader>
                            {isExpanded && (
                                <CardContent className="p-0 overflow-y-auto">
                                    <div className="bg-black/40 p-2 text-xs divide-y divide-white/5 shadow-inner">
                                        {Object.entries(categoriesData).map(([catName, data]) => (
                                            <div key={catName} className="p-3">
                                                <p className="text-indigo-400 font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.8)]"></div>
                                                    {catName.replace(/_/g, ' ')}
                                                </p>

                                                <div className="space-y-1.5 ml-3 font-medium text-gray-400">
                                                    {(data.botAtendimento > 0 || catName.includes('cupom')) && (
                                                        <div className="flex justify-between items-center bg-gray-500/10 px-2 py-1 rounded">
                                                            <span>Em atendimento (BOT)</span>
                                                            <span className="text-gray-300">{data.botAtendimento}</span>
                                                        </div>
                                                    )}
                                                    <div className="flex justify-between items-center hover:bg-white/5 px-2 py-1 rounded cursor-default border-l-2 border-yellow-500/50">
                                                        <span>Fila (Sem Resposta)</span>
                                                        <span className="text-yellow-400">{data.fila}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center hover:bg-white/5 px-2 py-1 rounded cursor-default border-l-2 border-purple-500/50">
                                                        <span>Ag. Usuário Responder</span>
                                                        <span className="text-purple-400">{data.aguardandoUser}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center hover:bg-white/5 px-2 py-1 rounded cursor-default border-l-2 border-pink-500/50">
                                                        <span>Ag. Staff Responder</span>
                                                        <span className="text-pink-400">{data.aguardandoStaff}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center hover:bg-white/5 px-2 py-1 rounded cursor-default border-l-2 border-emerald-500/50">
                                                        <span>Fechados / Resolvidos (24h)</span>
                                                        <span className="text-emerald-400">{data.fechados}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            )}
                        </Card>
                    );
                })}
            </div>

            {/* QUEUE FLOW CONTROL SETTINGS */}
            <Card className="bg-[#0F0F10] border-white/10 mt-8">
                <CardHeader className="border-b border-white/5 pb-4">
                    <CardTitle className="text-lg font-bold flex items-center gap-2">
                        <Settings className="w-5 h-5 text-gray-400" /> Controle de Fluxo da Fila (Queue Flow Control)
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                    <div className="space-y-6">
                        <div>
                            <p className="text-sm text-gray-400 mb-4">Configuração para segurar tickets de cidades específicas até que a fila baixe.</p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Threshold Input */}
                                <div className="space-y-3">
                                    <label className="text-sm font-bold text-white tracking-wide">Limiar de Liberação (Tickets Restantes)</label>
                                    <div className="flex items-center gap-3">
                                        <Input
                                            type="number"
                                            value={flowThreshold}
                                            onChange={(e) => setFlowThreshold(e.target.value)}
                                            className="bg-white/5 border-white/10 focus:border-blue-500 rounded-xl max-w-[150px]"
                                            min="0"
                                        />
                                        <span className="text-sm text-gray-500">tickets</span>
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        Quando houver Menos ou Igual a este número de tickets na Fila Global, as cidades restritas serão exibidas normalmente.
                                    </p>
                                </div>

                                {/* Cities Selection */}
                                <div className="space-y-3">
                                    <label className="text-sm font-bold text-white tracking-wide">Cidades com Fluxo Controlado (Ocultas)</label>
                                    <div className="flex flex-wrap gap-2">
                                        {availableCities.map(city => {
                                            const isSelected = restrictedCities.includes(city);
                                            return (
                                                <div
                                                    key={city}
                                                    onClick={() => {
                                                        if (isSelected) setRestrictedCities(prev => prev.filter(c => c !== city));
                                                        else setRestrictedCities(prev => [...prev, city]);
                                                    }}
                                                    className={`px-4 py-2 border rounded-xl text-sm font-bold cursor-pointer transition-all ${isSelected
                                                        ? 'bg-blue-500/20 border-blue-500 text-blue-400 shadow-[0_0_15px_rgba(59,130,246,0.2)]'
                                                        : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
                                                        }`}
                                                >
                                                    {city}
                                                </div>
                                            )
                                        })}
                                    </div>
                                    <p className="text-xs text-gray-500">Selecione as cidades que devem ficar invisíveis em "Novos Tickets" até que a fila caia sob o limiar.</p>
                                </div>
                            </div>
                        </div>

                        <div className="pt-4 border-t border-white/5 flex justify-end">
                            <Button
                                onClick={saveFlowSettings}
                                disabled={isSavingFlow}
                                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold w-[200px]"
                            >
                                {isSavingFlow ? 'Salvando...' : 'Salvar Regras'}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* STAFF ONLINE LIST */}
            <Card className="bg-[#0F0F10] border-white/10 mt-8">
                <CardHeader className="border-b border-white/5 pb-4">
                    <CardTitle className="text-lg font-bold flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Users className="w-5 h-5 text-gray-400" /> Staffs Trabalhando Agora
                        </div>
                        <span className="text-xs font-normal text-gray-500 bg-gray-800/50 px-2 py-1 rounded-md">
                            Ativo &lt; 15m | Ausente &lt; 30m
                        </span>
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 max-h-[400px] overflow-y-auto">
                    <div className="divide-y divide-white/5">
                        {isLoading ? (
                            <div className="p-8 text-center text-gray-500">Buscando atividade da staff...</div>
                        ) : staffList.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">Nenhum staff registrou atividade nos últimos 30 minutos.</div>
                        ) : (
                            staffList.map(staff => (
                                <div key={staff.id} className="p-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors">
                                    <div className="flex items-center gap-3">
                                        <div className="relative">
                                            <div className="w-10 h-10 rounded-full bg-gray-800 overflow-hidden border border-white/10">
                                                {staff.avatar ? (
                                                    <img src={`https://cdn.discordapp.com/avatars/${staff.discord_id}/${staff.avatar}.png`} className="w-full h-full object-cover" alt="" />
                                                ) : (
                                                    <Users className="w-5 h-5 text-gray-500 m-2.5" />
                                                )}
                                            </div>
                                            <span className={`absolute bottom-0 right-0 w-3 h-3 border-2 border-[#0F0F10] rounded-full ${getStatusColor(staff.status)}`} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-white">{getStaffDisplayName({ name: staff.name, customName: staff.username, ...staff })}</p>
                                            <p className="text-xs text-white/80 font-medium uppercase tracking-widest mt-0.5">{staff.access_level?.replace(/_/g, ' ')}</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <Badge variant="outline" className={`${staff.status === 'active' ? 'text-green-400 border-green-400/20' : 'text-yellow-400 border-yellow-400/20'} bg-transparent mb-1`}>
                                            {getStatusLabel(staff.status)}
                                        </Badge>
                                        <p className="text-[10px] text-gray-500 flex items-center justify-end gap-1">
                                            <Clock className="w-3 h-3" /> Última ação: {Math.floor(staff.diffMins)}m atrás
                                        </p>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* CUPOM 70 MODAL */}
            {isCupomModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-[#0f1115] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col"
                    >
                        <div className="p-5 border-b border-white/5 flex items-center justify-between bg-white/[0.02]">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-emerald-500/10 text-emerald-400 rounded-lg">
                                    <TicketPercent className="w-5 h-5" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-white">Gerenciar Cupom 70</h2>
                                    <p className="text-xs text-gray-400">Controle de abertura de novos tickets</p>
                                </div>
                            </div>
                            <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white pb-6" onClick={() => setIsCupomModalOpen(false)}>
                                <X className="w-5 h-5" />
                            </Button>
                        </div>

                        <div className="p-6 space-y-6 overflow-y-auto max-h-[70vh]">
                            {/* Status Atual Card */}
                            <div className="p-4 rounded-xl border border-white/5 bg-black/20 flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-400 font-medium">Status Atual na Criação</p>
                                    <p className="text-base font-bold text-white mt-1">
                                        {cupomSettings.status === 'active' && <span className="text-green-400 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Ativo e Disponível</span>}
                                        {cupomSettings.status === 'suspended_manual' && <span className="text-red-400 flex items-center gap-1.5"><X className="w-4 h-4" /> Suspenso Manualmente</span>}
                                        {cupomSettings.status === 'suspended_auto' && <span className="text-yellow-400 flex items-center gap-1.5"><Timer className="w-4 h-4" /> Automático / Com Tempo</span>}
                                    </p>
                                </div>
                            </div>

                            {/* Options */}
                            <div className="space-y-4">
                                <label className="text-sm font-bold text-white tracking-wide">Definir novo status:</label>
                                <div className="grid grid-cols-1 gap-3">
                                    <button
                                        onClick={() => setCupomSettings({ ...cupomSettings, status: 'active' })}
                                        className={`p-4 rounded-xl border text-left flex items-center gap-3 transition-all ${cupomSettings.status === 'active' ? 'bg-green-500/10 border-green-500/50 ring-1 ring-green-500/50' : 'bg-white/5 border-white/10 hover:border-white/20'}`}
                                    >
                                        <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${cupomSettings.status === 'active' ? 'border-green-500' : 'border-gray-500'}`}>
                                            {cupomSettings.status === 'active' && <div className="w-2 h-2 bg-green-500 rounded-full" />}
                                        </div>
                                        <div>
                                            <p className={`font-bold text-sm ${cupomSettings.status === 'active' ? 'text-green-400' : 'text-gray-300'}`}>Ativar Categoria (Normal)</p>
                                            <p className="text-xs text-gray-500 mt-1">A opção "Cupom" aparece normal para os jogadores abrirem ticket.</p>
                                        </div>
                                    </button>

                                    <button
                                        onClick={() => setCupomSettings({ ...cupomSettings, status: 'suspended_manual' })}
                                        className={`p-4 rounded-xl border text-left flex items-center gap-3 transition-all ${cupomSettings.status === 'suspended_manual' ? 'bg-red-500/10 border-red-500/50 ring-1 ring-red-500/50' : 'bg-white/5 border-white/10 hover:border-white/20'}`}
                                    >
                                        <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${cupomSettings.status === 'suspended_manual' ? 'border-red-500' : 'border-gray-500'}`}>
                                            {cupomSettings.status === 'suspended_manual' && <div className="w-2 h-2 bg-red-500 rounded-full" />}
                                        </div>
                                        <div>
                                            <p className={`font-bold text-sm ${cupomSettings.status === 'suspended_manual' ? 'text-red-400' : 'text-gray-300'}`}>Suspender Manualmente</p>
                                            <p className="text-xs text-gray-500 mt-1">O botão ficará escuro e listado como "ESGOTADO" até reativarem.</p>
                                        </div>
                                    </button>

                                    <button
                                        onClick={() => setCupomSettings({ ...cupomSettings, status: 'suspended_auto' })}
                                        className={`p-4 rounded-xl border text-left flex items-center gap-3 transition-all ${cupomSettings.status === 'suspended_auto' ? 'bg-yellow-500/10 border-yellow-500/50 ring-1 ring-yellow-500/50' : 'bg-white/5 border-white/10 hover:border-white/20'}`}
                                    >
                                        <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${cupomSettings.status === 'suspended_auto' ? 'border-yellow-500' : 'border-gray-500'}`}>
                                            {cupomSettings.status === 'suspended_auto' && <div className="w-2 h-2 bg-yellow-500 rounded-full" />}
                                        </div>
                                        <div className="flex-1 w-full">
                                            <p className={`font-bold text-sm ${cupomSettings.status === 'suspended_auto' ? 'text-yellow-400' : 'text-gray-300'}`}>Suspender Automaticamente</p>
                                            <p className="text-xs text-gray-500 mt-1 mb-3">A categoria se bloqueia dependendo da data/hora.</p>

                                            {cupomSettings.status === 'suspended_auto' && (
                                                <div className="space-y-3 p-3 bg-black/40 rounded-lg border border-yellow-500/20" onClick={(e) => e.stopPropagation()}>
                                                    <div>
                                                        <label className="text-xs font-semibold text-gray-400 block mb-1">Data e Hora da Suspensão</label>
                                                        <Input
                                                            type="datetime-local"
                                                            className="bg-[#0f1115] border-white/10 text-white h-9"
                                                            value={cupomSettings.autoSuspendAt}
                                                            onChange={(e) => setCupomSettings({ ...cupomSettings, autoSuspendAt: e.target.value })}
                                                        />
                                                    </div>
                                                    <label className="flex items-center gap-2 cursor-pointer mt-2">
                                                        <input
                                                            type="checkbox"
                                                            className="w-4 h-4 rounded border-white/20 bg-black text-yellow-500 focus:ring-yellow-500"
                                                            checked={cupomSettings.showTimer}
                                                            onChange={(e) => setCupomSettings({ ...cupomSettings, showTimer: e.target.checked })}
                                                        />
                                                        <span className="text-xs text-gray-300 font-medium">Exibir Cronômetro na tela de Tickets (contagem regressiva)</span>
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="p-5 border-t border-white/5 bg-white/[0.02] flex justify-end gap-3 shrink-0">
                            <Button variant="outline" className="border-white/10 hover:bg-white/10 text-white" onClick={() => setIsCupomModalOpen(false)}>Cancelar</Button>
                            <Button
                                onClick={saveCupomSettings}
                                disabled={isSavingCupom || (cupomSettings.status === 'suspended_auto' && !cupomSettings.autoSuspendAt)}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                                {isSavingCupom ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                                Salvar Configuração
                            </Button>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default TeamManagement;
